import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const SUMMARY_URL = 'accounts/admin/dmarc/summary/';
const REPORTS_URL = 'accounts/admin/dmarc/reports/';

export default function AdminDMARC() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, reportsRes] = await Promise.all([
        api.get(`${SUMMARY_URL}?days=${days}`),
        api.get(`${REPORTS_URL}?days=${days}`),
      ]);
      setSummary(summaryRes.data);
      setReports(reportsRes.data.reports || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login?next=/admin/dmarc'); return; }
    if (user && !user.is_staff && !user.is_superuser) { navigate('/'); return; }
    fetchData();
  }, [isAuthenticated, user, fetchData, navigate]);

  if (!isAuthenticated || (user && !user.is_staff && !user.is_superuser)) {
    return null;
  }

  const s = summary?.summary || {};
  const passRate = s.pass_rate || 100;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📧 DMARC Reports</h1>
        <div style={styles.controls}>
          <select 
            value={days} 
            onChange={(e) => setDays(parseInt(e.target.value))}
            style={styles.select}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={fetchData} style={styles.refreshBtn}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div style={styles.loading}>Loading DMARC data...</div>
      )}

      {error && (
        <div style={styles.error}>Error: {error}</div>
      )}

      {!loading && summary && (
        <>
          {/* Summary Cards */}
          <div style={styles.cardGrid}>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Total Reports</div>
              <div style={styles.cardValue}>{s.total_reports || 0}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Emails Checked</div>
              <div style={styles.cardValue}>{(s.total_emails || 0).toLocaleString()}</div>
            </div>
            <div style={{...styles.card, borderColor: passRate >= 95 ? '#22c55e' : passRate >= 80 ? '#eab308' : '#ef4444'}}>
              <div style={styles.cardLabel}>Pass Rate</div>
              <div style={{
                ...styles.cardValue,
                color: passRate >= 95 ? '#22c55e' : passRate >= 80 ? '#eab308' : '#ef4444'
              }}>
                {passRate}%
              </div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Failures</div>
              <div style={{...styles.cardValue, color: s.total_fail > 0 ? '#ef4444' : '#22c55e'}}>
                {s.total_fail || 0}
              </div>
            </div>
          </div>

          {/* Pass Rate Bar */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Authentication Success Rate</h3>
            <div style={styles.progressBar}>
              <div style={{
                ...styles.progressFill,
                width: `${passRate}%`,
                background: passRate >= 95 ? '#22c55e' : passRate >= 80 ? '#eab308' : '#ef4444'
              }} />
            </div>
            <div style={styles.progressLabels}>
              <span>✅ {s.total_pass || 0} passed</span>
              <span>❌ {s.total_fail || 0} failed</span>
            </div>
          </div>

          {/* By Organization */}
          {summary.by_organization && summary.by_organization.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>By Email Provider</h3>
              <div style={styles.table}>
                <div style={styles.tableHeader}>
                  <span style={{flex: 2}}>Provider</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Reports</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Emails</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Pass</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Fail</span>
                </div>
                {summary.by_organization.map((org, i) => (
                  <div key={i} style={styles.tableRow}>
                    <span style={{flex: 2}}>{org.org_name}</span>
                    <span style={{flex: 1, textAlign: 'right'}}>{org.report_count}</span>
                    <span style={{flex: 1, textAlign: 'right'}}>{org.email_count}</span>
                    <span style={{flex: 1, textAlign: 'right', color: '#22c55e'}}>{org.pass_count}</span>
                    <span style={{flex: 1, textAlign: 'right', color: org.fail_count > 0 ? '#ef4444' : '#666'}}>
                      {org.fail_count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Failures */}
          {summary.recent_failures && summary.recent_failures.length > 0 && (
            <div style={styles.section}>
              <h3 style={{...styles.sectionTitle, color: '#ef4444'}}>⚠️ Recent Failures</h3>
              <div style={styles.table}>
                <div style={styles.tableHeader}>
                  <span style={{flex: 2}}>Source IP</span>
                  <span style={{flex: 1}}>Domain</span>
                  <span style={{flex: 1}}>Provider</span>
                  <span style={{flex: 1}}>DKIM</span>
                  <span style={{flex: 1}}>SPF</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Count</span>
                </div>
                {summary.recent_failures.map((f, i) => (
                  <div key={i} style={{...styles.tableRow, background: 'rgba(239, 68, 68, 0.1)'}}>
                    <span style={{flex: 2, fontFamily: 'monospace'}}>{f.source_ip}</span>
                    <span style={{flex: 1}}>{f.domain}</span>
                    <span style={{flex: 1}}>{f.org}</span>
                    <span style={{flex: 1, color: f.dkim === 'pass' ? '#22c55e' : '#ef4444'}}>
                      {f.dkim}
                    </span>
                    <span style={{flex: 1, color: f.spf === 'pass' ? '#22c55e' : '#ef4444'}}>
                      {f.spf}
                    </span>
                    <span style={{flex: 1, textAlign: 'right'}}>{f.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reports List */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>All Reports ({reports.length})</h3>
            {reports.length === 0 ? (
              <div style={styles.emptyState}>
                No DMARC reports yet. Reports are automatically ingested daily.
              </div>
            ) : (
              <div style={styles.table}>
                <div style={styles.tableHeader}>
                  <span style={{flex: 2}}>Provider</span>
                  <span style={{flex: 2}}>Domain</span>
                  <span style={{flex: 2}}>Date Range</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Emails</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Pass</span>
                  <span style={{flex: 1, textAlign: 'right'}}>Fail</span>
                </div>
                {reports.map((r, i) => (
                  <div key={i} style={styles.tableRow}>
                    <span style={{flex: 2}}>{r.org_name}</span>
                    <span style={{flex: 2}}>{r.domain}</span>
                    <span style={{flex: 2, fontSize: '0.85em', color: '#888'}}>
                      {new Date(r.date_begin).toLocaleDateString()} - {new Date(r.date_end).toLocaleDateString()}
                    </span>
                    <span style={{flex: 1, textAlign: 'right'}}>{r.total_count}</span>
                    <span style={{flex: 1, textAlign: 'right', color: '#22c55e'}}>{r.pass_count}</span>
                    <span style={{flex: 1, textAlign: 'right', color: r.fail_count > 0 ? '#ef4444' : '#666'}}>
                      {r.fail_count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Domains Tracked */}
          {summary.domains && summary.domains.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Domains Tracked</h3>
              <div style={styles.domainList}>
                {summary.domains.map((d, i) => (
                  <span key={i} style={styles.domainBadge}>{d}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    color: '#fff',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    margin: 0,
    fontSize: '1.8rem',
    fontWeight: 700,
  },
  controls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #444',
    background: '#1a1a2e',
    color: '#fff',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  refreshBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #9370db, #ff7cde)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#888',
  },
  error: {
    padding: '16px',
    background: 'rgba(239, 68, 68, 0.15)',
    borderRadius: '8px',
    color: '#ef4444',
    marginBottom: '16px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    background: 'linear-gradient(145deg, rgba(30, 15, 50, 0.9), rgba(20, 10, 35, 0.95))',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    border: '1px solid rgba(147, 112, 219, 0.3)',
  },
  cardLabel: {
    fontSize: '0.85rem',
    color: '#888',
    marginBottom: '8px',
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#fff',
  },
  section: {
    background: 'linear-gradient(145deg, rgba(30, 15, 50, 0.9), rgba(20, 10, 35, 0.95))',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    border: '1px solid rgba(147, 112, 219, 0.2)',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#fff',
  },
  progressBar: {
    height: '12px',
    background: '#1a1a2e',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease',
  },
  progressLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    color: '#888',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tableHeader: {
    display: 'flex',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    fontSize: '0.8rem',
    color: '#888',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '8px',
    fontSize: '0.9rem',
    alignItems: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    fontStyle: 'italic',
  },
  domainList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  domainBadge: {
    padding: '6px 12px',
    background: 'rgba(147, 112, 219, 0.2)',
    borderRadius: '20px',
    fontSize: '0.85rem',
    color: '#9370db',
    border: '1px solid rgba(147, 112, 219, 0.4)',
  },
};
